<template>
  <v-dialog persistent v-model="dialog">
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      :timeout="snackbarTimeout"
      :top="true"
    >
      {{ snackbarMsg }}
      <v-btn dark text @click="showSnackbar = false">CLOSE</v-btn>
    </v-snackbar>
    <v-alert v-model="alert" :type="alertType" dismissible>{{ alertMessage }}</v-alert>

    <v-card flat tile v-if="shipment != null">
      <v-card-title class="dialog-header-title">
        <v-row no-gutters>
          <v-col class="d-flex justify-content-start"
            >Manage Shipment Information ({{ shipment.shipment_order_id }}) for
            {{ shipment.quotation.company.short_name }}</v-col
          >

          <v-col cols="1" class="d-flex justify-content-end">
            <v-btn icon x-small color="white">
              <v-icon size="40" class="pr-2" @click="onDialogClose">mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <v-row no-gutters>
        <v-col>
          <v-tabs light v-model="selectedTab" show-arrows>
            <v-tab class="tab-active-class font-weight-bold text-capitalize"
              >Shipping detail</v-tab
            >
            <v-tab class="tab-active-class font-weight-bold text-capitalize"
              >Booking information</v-tab
            >
            <v-tab
              v-if="is_fcl && show_haulage_transport"
              class="tab-active-class font-weight-bold text-capitalize"
              >Haulage information</v-tab
            >
            <v-tab
              v-if="(is_lcl || is_air) && show_haulage_transport"
              class="tab-active-class font-weight-bold text-capitalize"
              >Transport information</v-tab
            >
            <v-tab
              v-if="is_fcl || is_lcl"
              class="tab-active-class font-weight-bold text-capitalize"
              >Container information</v-tab
            >
            <v-tab class="tab-active-class font-weight-bold text-capitalize"
              >Assign Contacts</v-tab
            >

            <v-tab
              v-if="$root.is_admin"
              class="tab-active-class font-weight-bold text-capitalize"
              >Shipment Schedule</v-tab
            >

            <v-tab
              v-if="$root.is_super_admin"
              class="tab-active-class font-weight-bold error--text text-capitalize"
              >Delete Shipment</v-tab
            >

            <!-- Shipping detail -->
            <v-tab-item>
              <v-card flat v-if="shipping_detail != null" color="white">
                <v-card-title class="primary--text headline"
                  >Shipping Detail</v-card-title
                >

                <v-divider class="my-0"></v-divider>

                <v-card-text>
                  <v-row>
                    <v-col cols="10" sm="6" md="3">
                      <span>Bill of lading:</span>
                      <input
                        :readonly="!$root.is_admin"
                        v-model="shipping_detail.misc.bill_of_lading"
                        class="form-control"
                      />
                    </v-col>

                    <v-col cols="10" sm="6" md="3">
                      <span>Customer reference:</span>
                      <input
                        :readonly="!$root.is_admin"
                        v-model="shipping_detail.misc.customer_reference"
                        class="form-control"
                      />
                    </v-col>

                    <v-col cols="10" sm="6" md="3">
                      <span>HS code(s):</span>
                      <input v-model="shipping_detail.hs_code" class="form-control" />
                    </v-col>

                    <v-col cols="10" sm="6" md="3" v-if="$root.is_admin">
                      <span>MBL reference:</span>
                      <input
                        v-model="shipping_detail.misc.mbl_reference"
                        class="form-control"
                      />
                    </v-col>

                    <v-col cols="10" sm="6" md="4">
                      <span>Commodity:</span>
                      <v-textarea
                        class="wf-text-field"
                        auto-grow
                        outlined
                        rows="3"
                        row-height="20"
                        clearable
                        v-model="shipping_detail.commodity"
                      ></v-textarea>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                  <v-row class="ml-5" justify="center">
                    <v-btn
                      class="mr-2 font-weight-bold"
                      depressed
                      tile
                      small
                      color="primary"
                      @click="updateShipmentInformation(shipping_detail)"
                      >UPDATE</v-btn
                    >
                    <v-btn
                      class="ml-2 font-weight-bold"
                      depressed
                      tile
                      small
                      color="error"
                      @click="onDialogClose"
                      >CLOSE</v-btn
                    >
                  </v-row>
                </v-card-actions>
              </v-card>
            </v-tab-item>

            <!-- Booking Information -->
            <v-tab-item>
              <v-card flat v-if="booking_info != null">
                <v-card-title>
                  <v-row no-gutters>
                    <v-col>
                      <span class="primary--text headline">Booking Information</span>
                    </v-col>

                    <v-col class="d-flex justify-content-end pr-4">
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            :disabled="shipment.booking_confirmed"
                            v-if="$root.is_admin"
                            v-on="on"
                            outlined
                            color="primary"
                            class="font-weight-bold caption px-2"
                            @click="onCompleteBooking"
                          >
                            <v-icon left>mdi-file-check</v-icon>COMPLETE BOOKING
                          </v-btn>
                        </template>
                        Click to complete booking
                      </v-tooltip>
                    </v-col>
                  </v-row>
                </v-card-title>

                <v-divider class="my-0"></v-divider>

                <v-card-text>
                  <v-row>
                    <v-col cols="12" sm="6" md="4">
                      <span>Container operator code:</span>
                      <input
                        v-model="booking_info.booking_info.container_operator_code"
                        class="form-control"
                      />
                    </v-col>

                    <v-col cols="12" sm="6" md="4">
                      <span>Container operator:</span>
                      <input
                        v-model="booking_info.booking_info.container_operator"
                        class="form-control"
                      />
                    </v-col>

                    <v-col cols="12" sm="6" md="4">
                      <span>Container operator bs code:</span>
                      <input
                        v-model="booking_info.booking_info.container_operator_bs_code"
                        class="form-control"
                      />
                    </v-col>

                    <v-col cols="10" sm="6" md="4">
                      <span>Forwarding agent:</span>
                      <input
                        v-model="booking_info.booking_info.forwarding_agent"
                        class="form-control"
                      />
                    </v-col>

                    <v-col cols="10" sm="6" md="4">
                      <span>Booking reference:</span>
                      <input
                        v-model="booking_info.booking_info.booking_reference"
                        class="form-control"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                  <v-row class="ml-5" justify="center">
                    <v-btn
                      class="mx-2 font-weight-bold"
                      depressed
                      tile
                      small
                      color="primary"
                      @click="updateShipmentInformation(booking_info)"
                      >UPDATE</v-btn
                    >
                    <v-btn
                      class="mx-2 font-weight-bold"
                      depressed
                      tile
                      small
                      color="error"
                      @click="onDialogClose"
                      >CLOSE</v-btn
                    >
                  </v-row>
                </v-card-actions>
              </v-card>
            </v-tab-item>

            <!-- Haulage Information (FCL) -->
            <v-tab-item v-if="is_fcl && show_haulage_transport">
              <v-tabs centered fixed-tabs light height="40">
                <!-- Export -->
                <v-tab
                  v-if="export_required"
                  class="tab-active-class font-weight-bold text-capitalize"
                  >Haulage Export</v-tab
                >
                <!-- Import -->
                <v-tab
                  v-if="import_required"
                  class="tab-active-class font-weight-bold text-capitalize"
                  >Haulage Import</v-tab
                >

                <!-- Haulage Export -->
                <v-tab-item v-if="export_required">
                  <!-- if track export is true -->
                  <template v-if="track_export">
                    <v-card flat v-if="haulage_export_info != null">
                      <v-card-title class="py-2">
                        <v-row no-gutters>
                          <v-col
                            class="d-flex justify-content-start primary--text headline"
                            >Haulage Export Information</v-col
                          >
                          <v-col class="d-flex justify-content-end" v-if="$root.is_admin">
                            <v-tooltip left>
                              <template v-slot:activator="{ on }">
                                <v-btn
                                  v-on="on"
                                  rounded
                                  depressed
                                  small
                                  color="error"
                                  class="font-weight-bold pa-3"
                                  :disabled="include_haulage_export"
                                  @click="trackUntrackExportImport('export', false)"
                                >
                                  <v-icon size="17">mdi-pin-off</v-icon>Untrack
                                </v-btn>
                              </template>
                              Untrack haulage export in the milestone
                            </v-tooltip>
                          </v-col>
                        </v-row>
                      </v-card-title>

                      <v-divider class="my-0"></v-divider>

                      <v-card-text>
                        <v-row no-gutters>
                          <v-col>
                            <v-row>
                              <v-col>
                                <time-window
                                  v-model="
                                    haulage_export_info.haulage_export.delivery_schedule
                                  "
                                  schedule-description="Deliver container"
                                ></time-window>
                              </v-col>
                            </v-row>

                            <v-row>
                              <v-col>
                                <time-window
                                  v-model="
                                    haulage_export_info.haulage_export.pull_back_schedule
                                  "
                                  schedule-description="Pull-back container"
                                ></time-window>
                              </v-col>
                            </v-row>

                            <v-divider></v-divider>

                            <v-row>
                              <v-col>
                                <contact-layout
                                  v-model="haulage_export_info.haulage_export.contact"
                                  :companyInfo="company_info"
                                ></contact-layout>
                              </v-col>
                            </v-row>
                          </v-col>
                        </v-row>
                      </v-card-text>

                      <v-divider></v-divider>

                      <v-card-actions>
                        <v-row class="ml-5" justify="center">
                          <v-btn
                            class="mx-2 font-weight-bold"
                            depressed
                            tile
                            small
                            color="primary"
                            @click="updateShipmentInformation(haulage_export_info)"
                            >UPDATE</v-btn
                          >
                          <v-btn
                            class="mx-2 font-weight-bold"
                            depressed
                            tile
                            small
                            color="error"
                            @click="onDialogClose"
                            >CLOSE</v-btn
                          >
                        </v-row>
                      </v-card-actions>
                    </v-card>
                  </template>

                  <!-- if track export is false -->
                  <template v-else>
                    <v-card flat>
                      <v-card-title class="py-2">
                        <v-row no-gutters>
                          <v-col
                            class="d-flex justify-content-start primary--text headline"
                            >Haulage Export Information</v-col
                          >
                          <v-col class="d-flex justify-content-end" v-if="$root.is_admin">
                            <v-tooltip left>
                              <template v-slot:activator="{ on }">
                                <v-btn
                                  v-on="on"
                                  rounded
                                  depressed
                                  small
                                  color="primary"
                                  class="font-weight-bold pa-3"
                                  @click="trackUntrackExportImport('export', true)"
                                >
                                  <v-icon size="17">mdi-pin</v-icon>Track
                                </v-btn>
                              </template>
                              Track haulage export in the milestone
                            </v-tooltip>
                          </v-col>
                        </v-row>
                      </v-card-title>

                      <v-divider class="my-0"></v-divider>
                    </v-card>
                  </template>
                </v-tab-item>

                <!-- Haulage Import -->
                <v-tab-item v-if="import_required">
                  <!-- if track import is true -->
                  <template v-if="track_import">
                    <v-card flat v-if="haulage_import_info != null">
                      <v-card-title class="py-2">
                        <v-row no-gutters>
                          <v-col
                            class="d-flex justify-content-start primary--text headline"
                            >Haulage Import Information</v-col
                          >
                          <v-col class="d-flex justify-content-end" v-if="$root.is_admin">
                            <v-tooltip left>
                              <template v-slot:activator="{ on }">
                                <v-btn
                                  v-on="on"
                                  rounded
                                  depressed
                                  small
                                  color="error"
                                  class="font-weight-bold pa-3"
                                  :disabled="include_haulage_import"
                                  @click="trackUntrackExportImport('import', false)"
                                >
                                  <v-icon size="17">mdi-pin-off</v-icon>Untrack
                                </v-btn>
                              </template>
                              Untrack haulage import in the milestone
                            </v-tooltip>
                          </v-col>
                        </v-row>
                      </v-card-title>

                      <v-divider class="my-0"></v-divider>

                      <v-card-text>
                        <v-row no-gutters>
                          <v-col>
                            <v-row class="mb-4">
                              <v-col>
                                <time-window
                                  v-model="
                                    haulage_import_info.haulage_import.delivery_schedule
                                  "
                                  schedule-description="Deliver container"
                                ></time-window>
                              </v-col>
                            </v-row>

                            <v-row>
                              <v-col>
                                <time-window
                                  v-model="
                                    haulage_import_info.haulage_import.pull_back_schedule
                                  "
                                  schedule-description="Pull-back container"
                                ></time-window>
                              </v-col>
                            </v-row>

                            <v-divider></v-divider>

                            <v-row>
                              <v-col>
                                <contact-layout
                                  v-model="haulage_import_info.haulage_import.contact"
                                  :companyInfo="company_info"
                                ></contact-layout>
                              </v-col>
                            </v-row>
                          </v-col>
                        </v-row>
                      </v-card-text>

                      <v-divider></v-divider>

                      <v-card-actions>
                        <v-row class="ml-5" justify="center">
                          <v-btn
                            class="mx-2 font-weight-bold"
                            depressed
                            tile
                            small
                            color="primary"
                            @click="updateShipmentInformation(haulage_import_info)"
                            >UPDATE</v-btn
                          >
                          <v-btn
                            class="mx-2 font-weight-bold"
                            depressed
                            tile
                            small
                            color="error"
                            @click="onDialogClose"
                            >CLOSE</v-btn
                          >
                        </v-row>
                      </v-card-actions>
                    </v-card>
                  </template>

                  <!-- if track import is false -->
                  <template v-else>
                    <v-card flat>
                      <v-card-title class="py-2">
                        <v-row no-gutters>
                          <v-col
                            class="d-flex justify-content-start primary--text headline"
                            >Haulage Import Information</v-col
                          >
                          <v-col class="d-flex justify-content-end" v-if="$root.is_admin">
                            <v-tooltip left>
                              <template v-slot:activator="{ on }">
                                <v-btn
                                  v-on="on"
                                  rounded
                                  depressed
                                  small
                                  color="primary"
                                  class="font-weight-bold pa-3"
                                  @click="trackUntrackExportImport('import', true)"
                                >
                                  <v-icon size="17">mdi-pin</v-icon>Track
                                </v-btn>
                              </template>
                              Track haulage import in the milestone
                            </v-tooltip>
                          </v-col>
                        </v-row>
                      </v-card-title>

                      <v-divider class="my-0"></v-divider>
                    </v-card>
                  </template>
                </v-tab-item>
              </v-tabs>
            </v-tab-item>

            <!-- Transport Information (LCL | AIR)-->
            <v-tab-item v-if="(is_lcl || is_air) && show_haulage_transport">
              <v-tabs centered fixed-tabs light height="40">
                <!-- Export -->
                <v-tab
                  v-if="export_required"
                  class="tab-active-class font-weight-bold text-capitalize"
                  >Transport Export</v-tab
                >
                <!-- Import -->
                <v-tab
                  v-if="import_required"
                  class="tab-active-class font-weight-bold text-capitalize"
                  >Transport Import</v-tab
                >

                <!-- Transport Export -->
                <v-tab-item v-if="export_required">
                  <!-- if track export is true -->
                  <template v-if="track_export">
                    <v-card flat v-if="transport_export_info != null">
                      <v-card-title class="py-2">
                        <v-row no-gutters>
                          <v-col
                            class="d-flex justify-content-start primary--text headline"
                            >Transport Export Information</v-col
                          >
                          <v-col class="d-flex justify-content-end" v-if="$root.is_admin">
                            <v-tooltip left>
                              <template v-slot:activator="{ on }">
                                <v-btn
                                  v-on="on"
                                  rounded
                                  depressed
                                  small
                                  color="error"
                                  class="font-weight-bold pa-3"
                                  :disabled="include_transport_export"
                                  @click="trackUntrackExportImport('export', false)"
                                >
                                  <v-icon size="17">mdi-pin-off</v-icon>Untrack
                                </v-btn>
                              </template>
                              Untrack transport export in the milestone
                            </v-tooltip>
                          </v-col>
                        </v-row>
                      </v-card-title>

                      <v-divider class="my-0"></v-divider>

                      <v-card-text>
                        <v-row>
                          <v-col>
                            <time-window
                              v-model="transport_export_info.transport_export.schedule"
                              schedule-description="Pick up schedule"
                            ></time-window>
                          </v-col>
                        </v-row>

                        <v-divider></v-divider>

                        <v-row>
                          <v-col>
                            <contact-layout
                              v-model="transport_export_info.transport_export.contact"
                              :companyInfo="company_info"
                            ></contact-layout>
                          </v-col>
                        </v-row>

                        <v-divider></v-divider>
                      </v-card-text>

                      <v-divider class="my-0"></v-divider>

                      <v-card-actions>
                        <v-row class="ml-5" justify="center">
                          <v-btn
                            class="mx-2 font-weight-bold"
                            depressed
                            tile
                            small
                            color="primary"
                            @click="updateShipmentInformation(transport_export_info)"
                            >UPDATE</v-btn
                          >
                          <v-btn
                            class="mx-2 font-weight-bold"
                            depressed
                            tile
                            small
                            color="error"
                            @click="onDialogClose"
                            >CLOSE</v-btn
                          >
                        </v-row>
                      </v-card-actions>
                    </v-card>
                  </template>

                  <!-- if track export is false -->
                  <template v-else>
                    <v-card flat>
                      <v-card-title class="py-2">
                        <v-row no-gutters>
                          <v-col
                            class="d-flex justify-content-start primary--text headline"
                            >Transport Export Information</v-col
                          >
                          <v-col class="d-flex justify-content-end" v-if="$root.is_admin">
                            <v-tooltip left>
                              <template v-slot:activator="{ on }">
                                <v-btn
                                  v-on="on"
                                  rounded
                                  depressed
                                  small
                                  color="primary"
                                  class="font-weight-bold pa-3"
                                  @click="trackUntrackExportImport('export', true)"
                                >
                                  <v-icon size="17">mdi-pin</v-icon>Track
                                </v-btn>
                              </template>
                              Track transport export in the milestone
                            </v-tooltip>
                          </v-col>
                        </v-row>
                      </v-card-title>

                      <v-divider class="my-0"></v-divider>
                    </v-card>
                  </template>
                </v-tab-item>

                <!-- Transport Import -->
                <v-tab-item v-if="import_required">
                  <!-- if track import is true -->
                  <template v-if="track_import">
                    <v-card flat v-if="transport_import_info != null">
                      <v-card-title class="py-2">
                        <v-row no-gutters>
                          <v-col
                            class="d-flex justify-content-start primary--text headline"
                            >Transport Import Information</v-col
                          >
                          <v-col class="d-flex justify-content-end" v-if="$root.is_admin">
                            <v-tooltip left>
                              <template v-slot:activator="{ on }">
                                <v-btn
                                  v-on="on"
                                  rounded
                                  depressed
                                  small
                                  color="error"
                                  class="font-weight-bold pa-3"
                                  :disabled="include_transport_import"
                                  @click="trackUntrackExportImport('import', false)"
                                >
                                  <v-icon size="17">mdi-pin-off</v-icon>Untrack
                                </v-btn>
                              </template>
                              Untrack transport import in the milestone
                            </v-tooltip>
                          </v-col>
                        </v-row>
                      </v-card-title>

                      <v-divider class="my-0"></v-divider>

                      <v-card-text>
                        <v-row>
                          <v-col>
                            <time-window
                              v-model="transport_import_info.transport_import.schedule"
                              schedule-description="Deliver cargo"
                            ></time-window>
                          </v-col>
                        </v-row>

                        <v-divider></v-divider>

                        <v-row>
                          <v-col>
                            <contact-layout
                              v-model="transport_import_info.transport_import.contact"
                              :companyInfo="company_info"
                            ></contact-layout>
                          </v-col>
                        </v-row>

                        <v-divider></v-divider>
                      </v-card-text>

                      <v-divider class="my-0"></v-divider>

                      <v-card-actions>
                        <v-row class="ml-5" justify="center">
                          <v-btn
                            class="mx-2 font-weight-bold"
                            depressed
                            tile
                            small
                            color="primary"
                            @click="updateShipmentInformation(transport_import_info)"
                            >UPDATE</v-btn
                          >
                          <v-btn
                            class="mx-2 font-weight-bold"
                            depressed
                            tile
                            small
                            color="error"
                            @click="onDialogClose"
                            >CLOSE</v-btn
                          >
                        </v-row>
                      </v-card-actions>
                    </v-card>
                  </template>

                  <!-- if track import is false -->
                  <template v-else>
                    <v-card flat>
                      <v-card-title class="py-2">
                        <v-row no-gutters>
                          <v-col
                            class="d-flex justify-content-start primary--text headline"
                            >Transport Import Information</v-col
                          >
                          <v-col class="d-flex justify-content-end" v-if="$root.is_admin">
                            <v-tooltip left>
                              <template v-slot:activator="{ on }">
                                <v-btn
                                  v-on="on"
                                  rounded
                                  depressed
                                  small
                                  color="primary"
                                  class="font-weight-bold pa-3"
                                  @click="trackUntrackExportImport('import', true)"
                                >
                                  <v-icon size="17">mdi-pin</v-icon>Track
                                </v-btn>
                              </template>
                              Track transport import in the milestone
                            </v-tooltip>
                          </v-col>
                        </v-row>
                      </v-card-title>

                      <v-divider class="my-0"></v-divider>
                    </v-card>
                  </template>
                </v-tab-item>
              </v-tabs>
            </v-tab-item>

            <!-- Container Information -->
            <v-tab-item v-if="is_fcl || is_lcl">
              <v-card flat v-if="container_info != null">
                <v-card-title class="primary--text headline"
                  >Container Information</v-card-title
                >
                <v-divider class="my-0"></v-divider>
                <v-card-text class="py-1">
                  <v-row>
                    <v-col cols="6">
                      <v-row v-for="(con, i) in container_info.containers" :key="i">
                        <v-tooltip top>
                          <template v-slot:activator="{ on }">
                            <v-chip
                              v-on="on"
                              class="ma-2"
                              color="primary"
                              label
                              outlined
                              >{{ con.container_number || "NIL" }}</v-chip
                            >
                          </template>
                          <span>Container number</span>
                        </v-tooltip>

                        <v-tooltip top>
                          <template v-slot:activator="{ on }">
                            <v-chip v-on="on" class="ma-2" color="teal" label outlined>{{
                              con.seal_number || "NIL"
                            }}</v-chip>
                          </template>
                          <span>Seal number</span>
                        </v-tooltip>

                        <!-- Only admin can edit container -->
                        <v-chip
                          v-if="$root.is_admin"
                          class="ma-2"
                          color="primary"
                          label
                          @click="editContainerInfo(con)"
                        >
                          <v-avatar center>
                            <v-icon>mdi-pencil</v-icon>
                          </v-avatar>
                        </v-chip>
                      </v-row>
                    </v-col>

                    <v-divider vertical></v-divider>

                    <!-- Only admin can edit container -->
                    <v-col cols="4">
                      <v-row v-if="$root.is_admin">
                        <v-col>
                          <span>Container number:</span>
                          <input
                            :disabled="disabledContainerInfo"
                            v-model="container_information.container_number"
                            class="form-control"
                          />
                        </v-col>
                      </v-row>

                      <v-row v-if="$root.is_admin">
                        <v-col>
                          <span>Seal number:</span>
                          <input
                            :disabled="disabledContainerInfo"
                            v-model="container_information.seal_number"
                            class="form-control"
                          />
                        </v-col>
                      </v-row>

                      <v-row
                        v-if="is_fcl && shipment.quotation.transaction_type == 'IMPORT'"
                      >
                        <v-col>
                          <span>Container discharge date time:</span>
                          <flat-pickr
                            v-model="container_info.container_discharge_from_vessel_date"
                            class="form-control"
                            style="background: white"
                            placeholder="Select date time"
                            :config="config"
                            name="date"
                          ></flat-pickr>
                        </v-col>
                      </v-row>

                      <v-row
                        v-if="is_lcl && shipment.quotation.transaction_type == 'IMPORT'"
                      >
                        <v-col>
                          <span>Container unstuff date time:</span>
                          <flat-pickr
                            v-model="container_info.container_unstuff_datetime"
                            class="form-control"
                            style="background: white"
                            placeholder="Select date time"
                            :config="config"
                            name="date"
                          ></flat-pickr>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card-text>

                <v-divider></v-divider>

                <!-- Only admin can edit -->
                <v-card-actions>
                  <v-row class="ml-5" justify="center">
                    <v-btn
                      class="mx-2 font-weight-bold"
                      depressed
                      tile
                      small
                      color="primary"
                      @click="updateShipmentInformation(container_info)"
                      >UPDATE</v-btn
                    >
                    <v-btn
                      class="mx-2 font-weight-bold"
                      depressed
                      tile
                      small
                      color="error"
                      @click="onDialogClose"
                      >CLOSE</v-btn
                    >
                  </v-row>
                </v-card-actions>
              </v-card>
            </v-tab-item>

            <!-- Assign Contacts -->
            <v-tab-item>
              <v-row no-gutters>
                <v-col>
                  <v-tabs centered fixed-tabs light height="40">
                    <v-tab class="tab-active-class font-weight-bold text-capitalize"
                      >Shipper Information</v-tab
                    >
                    <v-tab class="tab-active-class font-weight-bold text-capitalize"
                      >Consignee Information</v-tab
                    >
                    <v-tab class="tab-active-class font-weight-bold text-capitalize"
                      >Notify Party Information</v-tab
                    >

                    <!-- Shipper Information -->
                    <v-tab-item>
                      <v-card flat v-if="shipper_info != null">
                        <v-card-title class="primary--text headline"
                          >Shipper Information</v-card-title
                        >
                        <v-divider class="my-0"></v-divider>
                        <v-card-text>
                          <v-row>
                            <v-col>
                              <contact-layout
                                v-model="shipper_info.shipper"
                                :companyInfo="company_info"
                              ></contact-layout>
                            </v-col>
                          </v-row>
                        </v-card-text>

                        <v-divider></v-divider>

                        <v-card-actions>
                          <v-row class="ml-5" justify="center">
                            <v-btn
                              class="mx-2 font-weight-bold"
                              depressed
                              tile
                              small
                              color="primary"
                              @click="updateShipmentInformation(shipper_info)"
                              >UPDATE</v-btn
                            >
                            <v-btn
                              class="mx-2 font-weight-bold"
                              depressed
                              tile
                              small
                              color="error"
                              @click="onDialogClose"
                              >CLOSE</v-btn
                            >
                          </v-row>
                        </v-card-actions>
                      </v-card>
                    </v-tab-item>

                    <!-- Consignee Information -->
                    <v-tab-item>
                      <v-card flat v-if="consignee_info != null">
                        <v-card-title class="primary--text headline"
                          >Consignee Information</v-card-title
                        >
                        <v-divider class="my-0"></v-divider>
                        <v-card-text>
                          <v-row>
                            <v-col>
                              <contact-layout
                                v-model="consignee_info.consignee"
                                :companyInfo="company_info"
                              ></contact-layout>
                            </v-col>
                          </v-row>
                        </v-card-text>

                        <v-divider></v-divider>

                        <v-card-actions>
                          <v-row class="ml-5" justify="center">
                            <v-btn
                              class="mx-2 font-weight-bold"
                              depressed
                              tile
                              small
                              color="primary"
                              @click="updateShipmentInformation(consignee_info)"
                              >UPDATE</v-btn
                            >
                            <v-btn
                              class="mx-2 font-weight-bold"
                              depressed
                              tile
                              small
                              color="error"
                              @click="onDialogClose"
                              >CLOSE</v-btn
                            >
                          </v-row>
                        </v-card-actions>
                      </v-card>
                    </v-tab-item>

                    <!-- Notify Party Information -->
                    <v-tab-item>
                      <v-card flat v-if="notify_party_info != null">
                        <v-card-title class="primary--text headline"
                          >Notify Party Information</v-card-title
                        >
                        <v-divider class="my-0"></v-divider>
                        <v-card-text>
                          <v-row>
                            <v-col>
                              <contact-layout
                                v-model="notify_party_info.notify_party"
                                :companyInfo="company_info"
                              ></contact-layout>
                            </v-col>
                          </v-row>
                        </v-card-text>

                        <v-divider></v-divider>

                        <v-card-actions>
                          <v-row class="ml-5" justify="center">
                            <v-btn
                              class="mx-2 font-weight-bold"
                              depressed
                              tile
                              small
                              color="primary"
                              @click="updateShipmentInformation(notify_party_info)"
                              >UPDATE</v-btn
                            >
                            <v-btn
                              class="mx-2 font-weight-bold"
                              depressed
                              tile
                              small
                              color="error"
                              @click="onDialogClose"
                              >CLOSE</v-btn
                            >
                          </v-row>
                        </v-card-actions>
                      </v-card>
                    </v-tab-item>
                  </v-tabs>
                </v-col>
              </v-row>
            </v-tab-item>

            <!-- Shipment Schedule Override -->
            <v-tab-item v-if="$root.is_admin">
              <v-card flat v-if="shipment_schedule_info != null">
                <v-card-title>
                  <v-row no-gutters>
                    <v-col cols="4">
                      <span class="primary--text headline">Shipment Schedule</span>
                    </v-col>
                  </v-row>
                </v-card-title>

                <v-divider class="my-0"></v-divider>

                <v-card-text>
                  <v-row>
                    <v-col>
                      <v-stepper dark>
                        <v-stepper-header class="mx-4">
                          <v-stepper-step
                            color="blue darken-1"
                            complete
                            complete-icon="mdi-ferry"
                            step="1"
                          >
                            <span class="blue--text text--darken-1 font-weight-bold">{{
                              shipment.quotation.origin_port.un_code
                            }}</span>
                            <small>{{ shipment.quotation.origin_port.name }}</small>
                          </v-stepper-step>

                          <v-divider></v-divider>

                          <v-stepper-step
                            color="green darken-1"
                            complete
                            complete-icon="mdi-anchor"
                            step="2"
                          >
                            <span class="green--text text--darken-1 font-weight-bold">{{
                              shipment.quotation.destination_port.un_code
                            }}</span>
                            <small>{{ shipment.quotation.destination_port.name }}</small>
                          </v-stepper-step>
                        </v-stepper-header>
                      </v-stepper>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="6" md="6">
                      <span>Origin ETA:</span>
                      <flat-pickr
                        v-model="shipment_schedule_info.shipment_schedule.origin_eta"
                        class="form-control"
                        :config="config"
                        style="background: white"
                        placeholder="Select date and time"
                        name="date"
                      ></flat-pickr>
                    </v-col>

                    <v-col cols="12" sm="6" md="6">
                      <span>Origin ETD:</span>
                      <flat-pickr
                        v-model="shipment_schedule_info.shipment_schedule.origin_etd"
                        class="form-control"
                        :config="config"
                        style="background: white"
                        placeholder="Select date and time"
                        name="date"
                      ></flat-pickr>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" sm="6" md="6">
                      <span>Destination ETA:</span>
                      <flat-pickr
                        v-model="shipment_schedule_info.shipment_schedule.destination_eta"
                        class="form-control"
                        :config="config"
                        style="background: white"
                        placeholder="Select date and time"
                        name="date"
                      ></flat-pickr>
                    </v-col>

                    <v-col cols="12" sm="6" md="6">
                      <span>Destination ETD:</span>
                      <flat-pickr
                        v-model="shipment_schedule_info.shipment_schedule.destination_etd"
                        class="form-control"
                        :config="config"
                        style="background: white"
                        placeholder="Select date and time"
                        name="date"
                      ></flat-pickr>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" sm="6" md="6">
                      <span>Vessel Name:</span>
                      <input
                        v-model="shipment_schedule_info.shipment_schedule.vessel_name"
                        class="form-control"
                      />
                    </v-col>

                    <v-col cols="12" sm="6" md="6">
                      <span>Vessel Voyage:</span>
                      <input
                        v-model="shipment_schedule_info.shipment_schedule.vessel_voyage"
                        class="form-control"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions>
                  <v-row class="ml-5" justify="center">
                    <v-btn
                      class="mx-2 font-weight-bold"
                      depressed
                      tile
                      small
                      color="primary"
                      @click="
                        updateShipmentScheduleOverride(
                          shipment_schedule_info.shipment_schedule
                        )
                      "
                      >UPDATE</v-btn
                    >
                    <v-btn
                      class="mx-2 font-weight-bold"
                      depressed
                      tile
                      small
                      color="error"
                      @click="onDialogClose"
                      >CLOSE</v-btn
                    >
                  </v-row>
                </v-card-actions>
              </v-card>
            </v-tab-item>

            <!-- Delete Shipment -->
            <v-tab-item v-if="$root.is_super_admin">
              <v-card flat>
                <v-card-title class="primary--text headline"
                  >Delete Shipment</v-card-title
                >

                <v-divider class="my-0"></v-divider>

                <v-card-text>
                  <v-alert outlined type="warning" prominent border="left"
                    >Deleting this shipment is a permanent action and you will lose every
                    saved information about this shipment.</v-alert
                  >
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions>
                  <v-row class="ml-5" justify="center">
                    <v-btn
                      class="ml-2 font-weight-bold"
                      depressed
                      tile
                      small
                      color="error"
                      @click="deleteShipmentInformation"
                      >DELETE SHIPMENT</v-btn
                    >
                  </v-row>
                </v-card-actions>
              </v-card>
            </v-tab-item>
          </v-tabs>
        </v-col>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
import constant from "../../const";
import TimeWindow from "@/components/shared/TimeWindow.vue";
import ContactLayout from "@/components/shared/ContactLayout.vue";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import "flatpickr/dist/themes/airbnb.css";

export default {
  props: ["value", "dialog", "tab"],
  components: {
    TimeWindow,
    flatPickr,
    ContactLayout,
  },
  data() {
    return {
      CONSTANTS: constant,
      snackbarColor: "",
      showSnackbar: false,
      snackbarMsg: "",
      snackbarTimeout: 6000,
      alert: false,
      alertType: "success",
      alertMessage: "",

      config: {
        altInput: true,
        altFormat: "M j, Y h:i K",
        altInputClass: "flatpickr-alt-input-custom",
        enableTime: true,
        dateFormat: "Y-m-d H:i:S",
        time_24hr: true,
        position: "auto",
      },

      selectedTab: 0,

      shipment_order_id: null,
      shipment: null,
      shipmentUpdated: false,
      updatedShipment: null,
      container_information: {
        container_number: null,
        seal_number: null,
      },
      disabledContainerInfo: true,
    };
  },
  // created() {},
  mounted() {
    if (this.tab != null) {
      this.selectedTab = this.tab;
    }
  },

  watch: {
    dialog() {
      if (this.dialog) {
        this.shipment_order_id = this.value;
        // initialise
        this.shipmentUpdated = false;
        this.updatedShipment = null;
        this.shipment = null;
        this.container_information = {
          container_number: null,
          seal_number: null,
        };
        this.disabledContainerInfo = true;
        this.selectedTab = this.tab;
        this.onInit();
      } else {
        this.shipment = null;
      }
    },
  },

  methods: {
    /**
     * initialise and get the shipment information
     */
    async onInit() {
      this.$root.blockUIshow = true;
      this.$root.blockUImsg = "Loading shipment information...";

      let resp = await this.$https.get(`/api/shipment_orders/${this.shipment_order_id}`);

      if (resp.data.status == constant.RESPONSE_STATUS_OK) {
        this.shipment = resp.data.data;
      } else {
        this.shipment = null;
      }

      this.$root.blockUIshow = false;
    },
    /**
     *  on complete booking
     */
    async onCompleteBooking() {
      this.$root.blockUImsg = "Completing booking process";
      this.$root.blockUIshow = true;

      let resp = await this.$https.put(
        `/api/shipment_orders/${this.shipment.shipment_order_id}/status/${constant.STATUS.SHIPMENT_ORDER.BOOKING_CONFIRMED}`
      );

      if (resp.data.status == constant.RESPONSE_STATUS_OK) {
        this.showSnackbar = true;
        this.snackbarColor = "success";
        this.snackbarMsg = "Booking completed successfully";
      } else {
        console.log(resp.data);
        this.showSnackbar = true;
        this.snackbarColor = "error";
        this.snackbarMsg = resp.data.msg;
      }

      this.$root.blockUIshow = false;
    },

    /**
     * Edit container information
     */
    editContainerInfo(item) {
      this.container_information = item;
      this.disabledContainerInfo = false;
    },

    /**
     * Update shipment information
     */
    async updateShipmentInformation(postform) {
      this.$root.blockUIshow = true;
      this.$root.blockUImsg = "Updating shipment information...";

      let resp = await this.$https.put(
        `/api/shipment_orders/${this.shipment.shipment_order_id}`,
        postform
      );

      if (resp.data.status == constant.RESPONSE_STATUS_OK) {
        this.shipmentUpdated = true;
        this.updatedShipment = resp.data.data;

        this.showSnackbar = true;
        this.snackbarColor = "success";
        this.snackbarMsg = resp.data.msg;
      } else {
        this.showSnackbar = true;
        this.snackbarColor = "error";
        this.snackbarMsg = resp.data.msg;
      }

      this.$root.blockUIshow = false;
    },

    /**
     * Delete shipment
     */
    async deleteShipmentInformation() {
      this.$root.blockUIshow = true;
      this.$root.blockUImsg = "Deleting shipment information...";

      let resp = await this.$https.delete(
        `/api/shipment_orders/${this.shipment.shipment_order_id}`
      );

      if (resp.data.status == constant.RESPONSE_STATUS_OK) {
        this.showSnackbar = true;
        this.snackbarColor = "success";
        this.snackbarMsg = resp.data.msg;

        // notify the shipment page
        this.onShipmentDeleted();
      } else {
        this.showSnackbar = true;
        this.snackbarColor = "error";
        this.snackbarMsg = resp.data.msg;
      }

      this.$root.blockUIshow = false;
    },

    /**
     * Update shipment information
     */
    async trackUntrackExportImport(track, status) {
      this.$root.blockUIshow = true;
      this.$root.blockUImsg = "Updating shipment information...";

      let postform = {
        track: track,
        status: status,
      };

      let resp = await this.$https.put(
        `/api/shipment_orders/${this.shipment.shipment_order_id}/track_haulage_transport`,
        postform
      );

      if (resp.data.status == constant.RESPONSE_STATUS_OK) {
        // this.shipmentUpdated = true
        if (postform.track == "export") {
          this.shipment.track_export = status;
        } else {
          this.shipment.track_import = status;
        }

        this.showSnackbar = true;
        this.snackbarColor = "success";
        this.snackbarMsg = resp.data.msg;
      } else {
        this.showSnackbar = true;
        this.snackbarColor = "error";
        this.snackbarMsg = resp.data.msg;
      }
      this.$root.blockUIshow = false;
    },

    /**
     * Update shipment Schedule override information
     */
    async updateShipmentScheduleOverride(postform) {
      this.$root.blockUIshow = true;
      this.$root.blockUImsg = "Updating shipment schedule information...";

      let resp = await this.$https.put(
        `/api/shipment_orders/shipment_schedules/${this.shipment.shipment_order_id}`,
        postform
      );
      if (resp.data.status == constant.RESPONSE_STATUS_OK) {
        this.shipmentUpdated = true;
        this.updatedShipment = resp.data.data;

        this.showSnackbar = true;
        this.snackbarColor = "success";
        this.snackbarMsg = resp.data.msg;
      } else {
        this.showSnackbar = true;
        this.snackbarColor = "error";
        this.snackbarMsg = resp.data.msg;
      }
      this.$root.blockUIshow = false;
    },

    /**
     * Close this dialog
     */
    onDialogClose() {
      this.$emit(
        "dialog-close",
        constant.DIALOG_SHIPMENT_INFORMARTION,
        this.updatedShipment,
        this.shipmentUpdated
      );
    },

    /**
     * on after shipment is deleted
     */
    onShipmentDeleted() {
      this.$emit("shipment-deleted");
    },
  },

  computed: {
    is_fcl() {
      return this.shipment.quotation_type == "FCL";
    },
    is_lcl() {
      return this.shipment.quotation_type == "LCL";
    },
    is_air() {
      return this.shipment.quotation_type == "AIR";
    },
    include_haulage_export() {
      return this.shipment.quotation.include_haulage_export;
    },
    include_haulage_import() {
      return this.shipment.quotation.include_haulage_import;
    },
    include_transport_export() {
      return this.shipment.quotation.include_transport_export;
    },
    include_transport_import() {
      return this.shipment.quotation.include_transport_import;
    },
    track_export() {
      return this.shipment.track_export;
    },
    track_import() {
      return this.shipment.track_import;
    },
    export_required() {
      return (
        this.shipment.quotation.require_haulage_export ||
        this.shipment.quotation.require_transport_export ||
        this.$root.is_admin
      );
    },
    import_required() {
      return (
        this.shipment.quotation.require_haulage_import ||
        this.shipment.quotation.require_transport_import ||
        this.$root.is_admin
      );
    },
    show_haulage_transport() {
      // if haulage || transport is required, then display
      return (
        this.$root.is_admin ||
        ((this.export_required || this.import_required) &&
          (this.track_export || this.track_import || this.$root.is_admin))
      );
    },

    company_info: {
      get() {
        if (this.shipment == null) {
          return null;
        }
        return this.shipment.quotation.company;
      },
    },
    /**
     * Computed Shipping detail
     */
    shipping_detail: {
      // getter
      get() {
        if (this.shipment == null) {
          return null;
        }

        return {
          hs_code: this.shipment.quotation.hs_code,
          commodity: this.shipment.quotation.commodity,
          misc: {
            bill_of_lading: this.shipment.misc.bill_of_lading,
            mbl_reference: this.shipment.misc.mbl_reference,
            customer_reference: this.shipment.misc.customer_reference,
          },
        };
      },
    },

    /**
     * Computed Booking information
     */
    booking_info: {
      // getter
      get() {
        if (this.shipment == null) {
          return null;
        }

        return {
          booking_info: this.shipment.booking_info,
        };
      },
    },

    /**
     * Computed Haulage export Information
     */
    haulage_export_info: {
      // getter
      get() {
        if (this.shipment == null) {
          return null;
        }

        return {
          haulage_export: this.shipment.haulage_export,
        };
      },
    },

    /**
     * Computed Haulage import Information
     */
    haulage_import_info: {
      // getter
      get() {
        if (this.shipment == null) {
          return null;
        }

        return {
          haulage_import: this.shipment.haulage_import,
        };
      },
    },

    /**
     * Computed Transport export Information
     */
    transport_export_info: {
      // getter
      get() {
        if (this.shipment == null) {
          return null;
        }

        return {
          transport_export: this.shipment.transport_export,
        };
      },
    },

    /**
     * Computed Transport import Information
     */
    transport_import_info: {
      // getter
      get() {
        if (this.shipment == null) {
          return null;
        }

        return {
          transport_import: this.shipment.transport_import,
        };
      },
    },

    /**
     * Computed Container Information
     */
    container_info: {
      // getter
      get() {
        if (this.shipment == null) {
          return null;
        }

        return {
          containers: this.shipment.containers,
          container_discharge_from_vessel_date: this.shipment
            .container_discharge_from_vessel_date,
          container_unstuff_datetime: this.shipment.container_unstuff_datetime,
        };
      },
    },

    /**
     * Computed Consignee Information
     */
    consignee_info: {
      // getter
      get() {
        if (this.shipment == null) {
          return null;
        }

        return {
          consignee: this.shipment.consignee,
        };
      },
    },

    /**
     * Computed Shipper Information
     */
    shipper_info: {
      // getter
      get() {
        if (this.shipment == null) {
          return null;
        }

        return {
          shipper: this.shipment.shipper,
        };
      },
    },

    /**
     * Computed Notify Party Information
     */
    notify_party_info: {
      // getter
      get() {
        if (this.shipment == null) {
          return null;
        }

        return {
          notify_party: this.shipment.notify_party,
        };
      },
    },

    /**
     * Computed Shipment Schedule Override Information
     */
    shipment_schedule_info: {
      // getter
      get() {
        if (this.shipment == null) {
          return null;
        }

        return {
          shipment_schedule: this.shipment.quotation.shipment_schedule,
        };
      },
    },
  },
};
</script>

<style scoped>
/* .v-tab.v-tab--active.tab-active-class {
  background-color: #607d8b;
} */

.v-input.v-text-field.wf-text-field {
  margin-top: 0px;
  padding-top: 0px;
  border: 0px !important;
  border-radius: 5px;
}

::v-deep .v-dialog {
  border-radius: 0px;
  margin: 0px;
  overflow-y: auto;
  pointer-events: auto;
  -webkit-transition: 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  width: 100%;
  height: 100%;
  z-index: inherit;
  -webkit-box-shadow: 0 11px 15px -7px rgba(0, 0, 0, 0.2),
    0 24px 38px 3px rgba(0, 0, 0, 0.14), 0 9px 46px 8px rgba(0, 0, 0, 0.12);
  box-shadow: 0 11px 15px -7px rgba(0, 0, 0, 0.2), 0 24px 38px 3px rgba(0, 0, 0, 0.14),
    0 9px 46px 8px rgba(0, 0, 0, 0.12);
}
</style>
